/* eslint-disable */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import DataTable from "../DataTable/DataTable";
import { getCommunitiesList, getStatisticsSuccess } from "../../../actions";
import { history } from "../../../config/stores";
import { API_URL } from "../../../config/settings";
import { User } from "../../../utilites/User";
import exportIcon from '../../../assets/icons/export_file_icon.svg';
import Header from "../../Header/Header";
import { tablePresets } from "../DataTable/tablePresets";
import * as XLSX from "xlsx";
import moment from "moment";
import { toast } from "react-toastify";
import "./CommunitiesList.scss";

const CommunitiesList = ({ getCommunitiesList, getStatisticsSuccess, communitiesList }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [countryFilter, setCountryFilter] = useState({
    countryName: "",
    countryCode: ""
  });
  const [cityFilter, setCityFilter] = useState("");
  
  // State for storing all communities data when filters are active
  const [allCommunitiesData, setAllCommunitiesData] = useState(null);
  const [isLoadingAllData, setIsLoadingAllData] = useState(false);
  
  // Progressive loading state (only used for unfiltered views now)
  const [isProgressiveLoading, setIsProgressiveLoading] = useState(false);
  const [progressiveLoadingStatus, setProgressiveLoadingStatus] = useState("");

  // Helper function to get country code from country name
  const getCountryCodeByName = (countryName) => {
    const countryMapping = {
      'Afghanistan': 'af', 'Albania': 'al', 'Algeria': 'dz', 'Argentina': 'ar', 'Australia': 'au',
      'Austria': 'at', 'Belgium': 'be', 'Brazil': 'br', 'Bulgaria': 'bg', 'Canada': 'ca',
      'China': 'cn', 'Croatia': 'hr', 'Cyprus': 'cy', 'Czech Republic': 'cz', 'Denmark': 'dk',
      'Egypt': 'eg', 'Finland': 'fi', 'France': 'fr', 'Germany': 'de', 'Greece': 'gr',
      'Hungary': 'hu', 'India': 'in', 'Ireland': 'ie', 'Italy': 'it', 'Japan': 'jp',
      'Netherlands': 'nl', 'Norway': 'no', 'Poland': 'pl', 'Portugal': 'pt', 'Romania': 'ro',
      'Russia': 'ru', 'Spain': 'es', 'Sweden': 'se', 'Switzerland': 'ch', 'Turkey': 'tr',
      'Ukraine': 'ua', 'United Kingdom': 'gb', 'United States': 'us', 'United States of America': 'us'
    };
    return countryMapping[countryName] || "";
  };

  // Load all communities data for filtered views (same approach as export)
  const loadAllCommunitiesForFilters = async () => {
    try {
      setIsLoadingAllData(true);
      console.log('Loading all communities for filtering...');
      
      // Use the same API call as export
      const response = await fetch(`${API_URL}/admin/dashboard/communities/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${User.getToken()}`
        },
        body: JSON.stringify({
          page: 1,
          limit: 10000 // High limit to get all communities
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const communities = result.payload?.docs || [];
      
      console.log(`Loaded ${communities.length} total communities for filtering`);
      setAllCommunitiesData(communities);
      setIsLoadingAllData(false);
      
      return communities;
      
    } catch (error) {
      console.error('Error loading all communities for filtering:', error);
      setIsLoadingAllData(false);
      setAllCommunitiesData([]);
      throw error;
    }
  };

  // Progressive loading function for filtered results (kept for reference but not used anymore)
  const loadPagesUntilSufficient = async (startPage = 1) => {
    let currentPageToLoad = startPage;
    
    const loadNextPage = () => {
      return new Promise((resolve, reject) => {
        // Removed the loading status message display
        
        const apiPayload = {
          page: currentPageToLoad,
          limit: 20,
          onSuccess: (response) => {
            console.log(`Progressive Loading - Page ${currentPageToLoad} loaded successfully`);
            resolve(response);
          },
          onError: (error) => {
            console.error('Progressive loading error:', error);
            reject(error);
          }
        };
        
        getCommunitiesList(apiPayload);
      });
    };

    const getCurrentFilteredCount = () => {
      if (!communitiesList?.docs) return 0;
      
      return communitiesList.docs.filter(community => {
        const matchesCountry = !countryFilter.countryCode || community.countryCode === countryFilter.countryCode;
        const matchesCity = !cityFilter || (community.cityEN && community.cityEN.toLowerCase().includes(cityFilter.toLowerCase()));
        return matchesCountry && matchesCity;
      }).length;
    };

    try {
      // Load pages until we have enough results or hit limits
      while (currentPageToLoad <= maxPagesToLoad) {
        await loadNextPage();
        
        // Wait a bit for Redux state to update
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const currentFilteredCount = getCurrentFilteredCount();
        console.log(`Progressive Loading - Page ${currentPageToLoad}: Found ${currentFilteredCount} total matches so far`);
        
        currentPageToLoad++;
        setCurrentPage(currentPageToLoad);
        
        // Check if we have enough results or no more pages
        if (currentFilteredCount >= targetResultCount || !communitiesList?.hasNextPage) {
          console.log(`Stopping progressive loading: Results=${currentFilteredCount}, HasNextPage=${communitiesList?.hasNextPage}`);
          break;
        }
        
        // Small delay to prevent overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const finalCount = getCurrentFilteredCount();
      setProgressiveLoadingStatus("");
      setIsProgressiveLoading(false);
      setIsLoading(false);
      
      console.log(`Progressive Loading Complete: Found ${finalCount} communities for ${countryFilter.countryName} after ${currentPageToLoad - startPage} additional pages`);
      
    } catch (error) {
      console.error('Progressive loading failed:', error);
      setProgressiveLoadingStatus("");
      setIsProgressiveLoading(false);
      setIsLoading(false);
    }
  };

  // Parse URL parameters for country and city filtering
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const countryName = urlParams.get('country') || "";
    const countryCode = countryName ? getCountryCodeByName(countryName) : "";
    const cityName = urlParams.get('city') || "";
    
    console.log('URL Country:', countryName, 'Mapped Code:', countryCode, 'City:', cityName); // Debug log
    
    setCountryFilter({
      countryName,
      countryCode
    });
    setCityFilter(cityName);
  }, []);

  useEffect(() => {
    // Set loading state and fetch communities list
    getStatisticsSuccess({ type: "communitiesList", loading: true });
    setCurrentPage(1); // Reset to page 1
    setIsLoading(true);
    setIsProgressiveLoading(false);
    setProgressiveLoadingStatus("");
    setAllCommunitiesData(null); // Reset all communities data

    if (countryFilter.countryCode || cityFilter) {
      // With filter: Load all data directly (same approach as export)
      console.log('Loading all communities for filtering by country:', countryFilter.countryName, 'city:', cityFilter);
      
      loadAllCommunitiesForFilters()
        .then(() => {
          setIsLoading(false);
          console.log('All communities loaded for filtering');
        })
        .catch((error) => {
          console.error('Failed to load communities for filtering:', error);
          setIsLoading(false);
        });
      
    } else {
      // No filter: Normal pagination using Redux
      const apiPayload = {
        page: 1,
        limit: 20,
        onSuccess: () => setIsLoading(false),
        onError: () => setIsLoading(false)
      };
      
      console.log('API Payload (no filter):', apiPayload);
      getCommunitiesList(apiPayload);
    }
  }, [countryFilter, cityFilter]); // Re-run when country or city filter changes

  // Filter data on frontend - uses allCommunitiesData for filtered views, Redux data for unfiltered
  const getFilteredData = () => {
    // For filtered views, use allCommunitiesData (same as export)
    if (countryFilter.countryCode || cityFilter) {
      if (!allCommunitiesData) {
        return { docs: [], totalDocs: 0, hasNextPage: false };
      }

      // Apply filters to all communities data
      const filteredDocs = allCommunitiesData.filter(community => {
        const matchesCountry = !countryFilter.countryCode || community.countryCode === countryFilter.countryCode;
        const matchesCity = !cityFilter || (community.cityEN && community.cityEN.toLowerCase().includes(cityFilter.toLowerCase()));
        return matchesCountry && matchesCity;
      });
      
      console.log('Filtering all communities by country code:', countryFilter.countryCode, 'and city:', cityFilter, 'Found:', filteredDocs.length, 'communities');
      
      return {
        docs: filteredDocs,
        totalDocs: filteredDocs.length,
        hasNextPage: false, // No pagination needed since we have all data
        loading: isLoadingAllData
      };
    }
    
    // For unfiltered views, use Redux data (normal pagination)
    if (!communitiesList?.docs) {
      return { docs: [], totalDocs: 0, hasNextPage: false };
    }

    return {
      ...communitiesList,
      docs: communitiesList.docs,
      totalDocs: communitiesList.docs.length,
      hasNextPage: communitiesList?.hasNextPage
    };
  };

  const handleLoadMore = () => {
    // For filtered views, no load more needed since we already have all data
    if (countryFilter.countryCode || cityFilter) {
      console.log('Load More not needed for filtered views - all data already loaded');
      return;
    }
    
    // For unfiltered views: Normal pagination
    if (isLoading || !communitiesList?.hasNextPage) {
      return; // Don't load if already loading or no more pages
    }

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    setIsLoading(true);
    
    const apiPayload = {
      page: nextPage,
      limit: 20,
      onSuccess: () => setIsLoading(false),
      onError: () => setIsLoading(false)
    };

    console.log('Load More Payload (no filter):', apiPayload);
    getCommunitiesList(apiPayload);
  };

  const handleBackToDashboard = () => {
    history.push('/');
  };

  // Handle Excel export with filtered data
  const handleExport = async () => {
    if (isExporting) return; // Prevent multiple exports
    
    try {
      setIsExporting(true);
      
      let allData = [];
      
      // For filtered views, use already loaded data if available
      if (countryFilter.countryCode || cityFilter) {
        if (allCommunitiesData) {
          // Use already loaded data
          allData = allCommunitiesData;
          console.log('Export: Using already loaded communities data');
        } else {
          // Load data if not already loaded
          allData = await loadAllDataDirectly();
        }
      } else {
        // For unfiltered views, load all data fresh
        allData = await loadAllDataDirectly();
      }
      
      if (!allData || allData.length === 0) {
        toast.warning('No data to export');
        return;
      }

      // Apply frontend filters if they exist (since backend doesn't filter yet)
      let filteredData = allData;
      if (countryFilter.countryCode || cityFilter) {
        filteredData = allData.filter(community => {
          const matchesCountry = !countryFilter.countryCode || community.countryCode === countryFilter.countryCode;
          const matchesCity = !cityFilter || (community.cityEN && community.cityEN.toLowerCase().includes(cityFilter.toLowerCase()));
          return matchesCountry && matchesCity;
        });
      }

      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Define headers matching the table columns
      const headers = [
        'Community Name',
        'Country',
        'City', 
        'Participants',
        'Added Properties',
        'Active Properties',
        'Admins',
        'Added On',
        'Added By',
        'Storage Used'
      ];
      
      // Transform data rows
      const rows = filteredData.map(community => [
        community.nickname || '---',
        tablePresets.getCountryName(community.countryCode),
        community.cityEN || '---',
        community.membersCount || 0,
        community.propertiesCount || 0,
        community.activePropertiesCount || 0,
        community.adminsCount || 0,
        community.createdAt ? new Date(community.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---',
        community.createdBy ? (community.createdBy.fullName || community.createdBy.email || '---') : 'Not available',
        community.usedStorage || '0'
      ]);
      
      // Create worksheet
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([headers, ...rows]),
        "Communities"
      );
      
      // Generate filename with current date and filter info
      let filename = `communities-${moment().format("DD-MM-YYYY")}`;
      
      if (countryFilter.countryName || cityFilter) {
        filename += '-filtered';
        if (countryFilter.countryName) {
          filename += `-${countryFilter.countryName.replace(/\s+/g, '-').toLowerCase()}`;
        }
        if (cityFilter) {
          filename += `-${cityFilter.replace(/\s+/g, '-').toLowerCase()}`;
        }
      }
      
      filename += '.xlsx';
      
      // Download file
      XLSX.writeFile(wb, filename);
      
      // Show success message
      toast.success(`Exported ${rows.length} communities to ${filename}`);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Load all data directly from API with high limit to get everything
  const loadAllDataDirectly = async () => {
    try {
      console.log('Export: Loading all communities with high limit...');
      
      // Use the same API URL and headers as the Redux epic
      const response = await fetch(`${API_URL}/admin/dashboard/communities/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${User.getToken()}`
        },
        body: JSON.stringify({
          page: 1,
          limit: 10000 // High limit to get all communities
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const communities = result.payload?.docs || [];
      
      console.log(`Export: Loaded ${communities.length} communities from API`);
      return communities;
      
    } catch (error) {
      console.error('Export: Error loading all communities:', error);
      throw error;
    }
  };

  return (
    <>
      <Header />
      <div className="main-container">
        <div className="inner-header-container">
          <div className="left-part">
            <div 
              className="close-icon-styles-communities" 
              onClick={handleBackToDashboard}
              title="Back to Dashboard"
            >
              <span className="close-icon-text">X</span>
            </div>
            <div className="inner-title">NEIGHBOURHOOD COMMUNITIES</div>
          </div>
          <div className="right-part">
            {(countryFilter.countryName || cityFilter) && (
              <div className="filter-info">
                <span className="filter-label">FILTERED BY:</span>
                <span className="filter-value">
                  {countryFilter.countryName && `COUNTRY - ${countryFilter.countryName.toUpperCase()}`}
                  {countryFilter.countryName && cityFilter && ' | '}
                  {cityFilter && `CITY - ${cityFilter.toUpperCase()}`}
                </span>
              </div>
            )}
            <img 
              src={exportIcon} 
              alt="Export" 
              className="export-icon-styles" 
              style={{ 
                cursor: isExporting ? "not-allowed" : "pointer",
                opacity: isExporting ? 0.5 : 1 
              }}
              onClick={handleExport}
              title={isExporting ? "Exporting..." : "Export to Excel"}
            />
          </div>
        </div>
        <div className="body-container">
          <DataTable
            data={getFilteredData()}
            preset="communitiesList"
            isLoading={
              (countryFilter.countryCode || cityFilter) 
                ? isLoadingAllData || isLoading 
                : communitiesList?.loading || isLoading
            }
            onLoadMore={handleLoadMore}
            hasMore={getFilteredData().hasNextPage}
            emptyStateText={
              (countryFilter.countryName || cityFilter) ? 
                `No communities found${countryFilter.countryName ? ` in ${countryFilter.countryName}` : ''}${cityFilter ? ` for city "${cityFilter}"` : ''}` : 
                "No communities found"
            }
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  communitiesList: state.dashboard.communitiesList,
});

const mapDispatchToProps = (dispatch) => ({
  getCommunitiesList: (payload) => dispatch(getCommunitiesList(payload)),
  getStatisticsSuccess: (payload) => dispatch(getStatisticsSuccess(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CommunitiesList);
